import dayjs from 'dayjs';
import { calculateAverageAuditTime } from '../../../../utils/timeUtils';
import { productList, normalizeProductCode } from '../../../common';

/**
 * Products unavailable in BOTH the warehouse inventory and the store shelf of a store check
 * (normalized to tolerate case/whitespace drift in inventory codes).
 */
export function getOutOfStockProducts(storeCheck) {
    const unavailableInventory = new Set(
        storeCheck?.inventory?.filter((i) => !i?.isAvailable)?.map((p) => normalizeProductCode(p?.productId)) ?? []
    );
    const unavailableProducts = storeCheck?.products?.filter((i) => !i?.isAvailable)?.map((p) => p?.name) ?? [];

    return (
        productList?.filter((p) => {
            const notInInventory = unavailableInventory.has(normalizeProductCode(p?.value));
            const notInProducts = unavailableProducts.includes(p?.name);
            return notInInventory && notInProducts;
        }) ?? []
    );
}

/** KPIs for the store detail header, computed from the loaded store checks. */
export function computeStoreDetailAnalytics(storeChecks, avgAuditTimeProp) {
    const totalAudits = storeChecks?.length || 0;
    const avgAuditTime = avgAuditTimeProp ?? calculateAverageAuditTime(storeChecks);

    // Last audit
    const lastAudit = storeChecks && storeChecks.length > 0 ? storeChecks[storeChecks.length - 1] : null;
    const lastAuditDate = lastAudit ? dayjs(lastAudit.endTime) : null;
    const daysSinceLastAudit = lastAuditDate ? dayjs().diff(lastAuditDate, 'days') : null;

    // Product availability from last audit
    const availableProducts = lastAudit?.products?.filter((p) => p.isAvailable)?.length || 0;
    const totalProducts = lastAudit?.products?.length || 0;
    const availabilityRate = totalProducts > 0 ? Math.round((availableProducts / totalProducts) * 100) : 0;

    // Inventory issues
    const emergencyInventory = lastAudit?.inventory?.filter((i) => i.isEmergency)?.length || 0;

    // Out of stock
    const outOfStock = getOutOfStockProducts(lastAudit).length || 0;

    // Compliance
    const productsWithPrice = lastAudit?.products?.filter((p) => p.price && p.price > 0)?.length || 0;
    const priceCompliance = totalProducts > 0 ? Math.round((productsWithPrice / totalProducts) * 100) : 0;

    const timeTrend = avgAuditTime <= 20 ? 'good' : avgAuditTime <= 30 ? 'warning' : null;
    const availabilityTrend = availabilityRate >= 80 ? 'good' : availabilityRate >= 60 ? 'warning' : 'bad';
    const complianceTrend = priceCompliance >= 90 ? 'good' : priceCompliance >= 70 ? 'warning' : 'bad';

    const lastAuditLabel = lastAuditDate ? (daysSinceLastAudit === 0 ? 'Última: Hoy' : `Última: Hace ${daysSinceLastAudit} días`) : null;

    return {
        totalAudits,
        avgAuditTime,
        lastAuditLabel,
        availableProducts,
        totalProducts,
        availabilityRate,
        emergencyInventory,
        outOfStock,
        productsWithPrice,
        priceCompliance,
        timeTrend,
        availabilityTrend,
        complianceTrend
    };
}
